import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import {
  bulkDeleteTestSteps,
  bulkUpdateManualTestSteps,
  bulkUpdateSteps,
  bulkUpdateTestSteps,
  copyTestCaseAsStepGroup,
  createBulkFolder,
  createFolder,
  creatElement,
  createSubFolder,
  createTestCaseComments,
  createTestCasetype,
  createTestSteps,
  deleteElementById,
  deleteFolder,
  deleteTestCaseComments,
  deleteTestCasetypebyId,
  deleteTestStepById,
  deletTestCaseById,
  exportSelectedTestCasesExcel,
  exportStepGroupsXml,
  exportTestCasesExcel,
  fetchRestResponse,
  importTestCasesExcel,
  saveTestCase,
  togglePinComment,
  updateElement,
  updateFolder,
  updateTestCase,
  updateTestCaseReviewer,
  updateTestCaseComments,
  updateTestCasetypesById,
  updateTestSteps,
  associateTestCaseTags,
  associateDefectTags,
  createManualTestRun,
  updateManualTestRunStep,
  completeManualTestRun,
  deleteManualTestRun,
  uploadManualRunImage,
  moveExplorerItems,
  MoveExplorerItemsPayload,
} from "./scenarios.api";

import type { TestSteps } from "@/common/types/generated";

/**
 * Invalidate every test-case list cache after a create/update/delete/clone.
 * Centralised so all CRUD mutations refresh the same set of keys — previously
 * each mutation invalidated an inconsistent subset (often only "allTestCases"),
 * leaving the type-specific tab lists and the folder-tree list stale.
 *
 * Note: the folder/tree infinite list uses the "allFolderTestCases" key (it was
 * split off from the plain "allTestCases" key to avoid a useQuery/useInfiniteQuery
 * cache-shape collision).
 */
const TEST_CASE_LIST_KEYS = [
  "allTestCases",
  "allFolderTestCases",
  "versionTestCasesAll",
  "manualTestCases",
  "automatedTestCases",
  "performanceTestCases",
  "versionTestCasesAll",
] as const;

const invalidateTestCaseLists = (queryClient: QueryClient) => {
  for (const key of TEST_CASE_LIST_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
  // Folder count badges are a view over the test-case lists — refresh the
  // folder list so per-folder counts can't drift after a create/delete/clone/move.
  queryClient.invalidateQueries({ queryKey: ["folderList"] });
};

/**
 * Invalidate the folder-tree caches after a folder create/delete/rename/move.
 * Centralised here so React Query is the single source of truth for folder
 * freshness — callers no longer fire an imperative refetch alongside their own
 * optimistic tree mutation (that dual source caused flicker / dropped-node
 * races). Partial keys match every variant (different versionId/type/countType)
 * so sibling consumers (case picker, requirement tree, create-folder dialog)
 * also refresh.
 */
const FOLDER_TREE_KEYS = [
  "folderList",
  "subFolderList",
  "folderDetails",
] as const;

const invalidateFolderTree = (queryClient: QueryClient) => {
  for (const key of FOLDER_TREE_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
};

export const useSaveTestCase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => saveTestCase(payload),
    onSuccess: () => {
      invalidateTestCaseLists(queryClient);
      // Invalidate module types cache in case a new module type was created
      queryClient.invalidateQueries({ queryKey: ["moduleTypes"] });
    },
  });
};

export const useCopyTestCaseAsStepGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      testCaseId: number;
      stepIds: number[];
      isStepGroup: boolean;
      isReplace: boolean;
    }) => copyTestCaseAsStepGroup(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["testSteps", String(variables.testCaseId)],
      });
      queryClient.invalidateQueries({ queryKey: ["stepGroups"] });
      invalidateTestCaseLists(queryClient);
    },
  });
};

export const useCreateElement = () => {
  return useMutation({
    mutationFn: (payload: any) => creatElement(payload),
  });
};
export const useCreateTestCaseType = () => {
  return useMutation({
    mutationFn: (payload: any) => createTestCasetype(payload),
  });
};
export const useUpdateElement = (elementId: string) => {
  return useMutation({
    mutationFn: (payload: any) => updateElement(elementId, payload),
  });
};

export const useUpdateTestCase = (testCaseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => updateTestCase(testCaseId, payload),
    onSuccess: () => {
      // Previously this mutation invalidated nothing, so after editing a test
      // case the list and detail caches stayed stale (up to staleTime=5min),
      // making a successful save look like it had silently failed.
      invalidateTestCaseLists(queryClient);
      // ACTIVELY refetch the detail (not just invalidate) so every consumer —
      // the inline context panel AND the edit dialog (which unmounts on close,
      // losing its observer) — shows the saved values immediately on reopen
      // instead of stale-while-revalidate data. `refetchQueries` runs the
      // queryFn now rather than waiting for the next mount.
      queryClient.refetchQueries({
        queryKey: ["testCaseDetails", String(testCaseId)],
      });
      // Tags live in a separate cache; refresh so the Tags field reflects edits.
      queryClient.invalidateQueries({
        queryKey: ["testCaseTagsById", String(testCaseId)],
      });
      // Steps, activity/history feed also reference the case.
      queryClient.invalidateQueries({
        queryKey: ["testSteps", String(testCaseId)],
      });
      queryClient.invalidateQueries({ queryKey: ["testCaseActivity"] });
      queryClient.invalidateQueries({
        queryKey: ["testCaseRunHistory", testCaseId],
      });
      // Invalidate module types cache in case a new module type was created
      queryClient.invalidateQueries({ queryKey: ["moduleTypes"] });
    },
  });
};

// Narrow mutation for reassigning ONLY the Reviewer — usable by the
// currently-assigned Reviewer even when they aren't the test case's creator.
export const useUpdateTestCaseReviewer = (testCaseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reviewerid: number | null) =>
      updateTestCaseReviewer(testCaseId, reviewerid),
    onSuccess: () => {
      invalidateTestCaseLists(queryClient);
      queryClient.refetchQueries({
        queryKey: ["testCaseDetails", String(testCaseId)],
      });
      queryClient.invalidateQueries({ queryKey: ["testCaseActivity"] });
    },
  });
};

export const useAssociateTestCaseTags = (testCaseId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagNames: string[]) =>
      associateTestCaseTags(testCaseId, tagNames),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["testCaseTagsById", String(testCaseId)],
      });
      queryClient.invalidateQueries({ queryKey: ["testTags"] });
    },
  });
};

export const useAssociateDefectTags = (defectId: number | string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagNames: string[]) => associateDefectTags(defectId, tagNames),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["defectTagsById", String(defectId)],
      });
      queryClient.invalidateQueries({ queryKey: ["defectTags"] });
    },
  });
};

export const useCreateFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createFolder(payload),
    onSuccess: () => {
      invalidateFolderTree(queryClient);
    },
  });
};

export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (folderId: number | string) => deleteFolder(folderId),
    onSuccess: () => {
      invalidateFolderTree(queryClient);
      // A deleted folder takes its test cases with it — refresh case lists too.
      invalidateTestCaseLists(queryClient);
      // …and any open test-case detail, so an inline panel showing a case that
      // lived in the deleted folder refetches, 404s, and auto-closes instead of
      // lingering on a now-deleted case.
      queryClient.invalidateQueries({ queryKey: ["testCaseDetails"] });
      // …and its requirements — refresh requirement lists too.
      queryClient.invalidateQueries({ queryKey: ["allRequirements"] });
    },
  });
};

export const useCreateSubFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createSubFolder(payload),
    onSuccess: () => {
      invalidateFolderTree(queryClient);
    },
  });
};

export const useCreateBulkFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createBulkFolder(payload),
    onSuccess: () => {
      // AI generation can create/reuse folders and test cases anywhere in the
      // tree — refresh the folder tree and case lists so they appear immediately.
      invalidateFolderTree(queryClient);
      queryClient.invalidateQueries({ queryKey: ["allFolderTestCases"] });
      queryClient.invalidateQueries({ queryKey: ["allTestCases"] });
      queryClient.invalidateQueries({ queryKey: ["versionTestCasesAll"] });
    },
  });
};

/**
 * Fire-and-forget save for AI-generated test cases that must complete even after
 * the generator modal is closed/unmounted. Returns a plain function that kicks
 * off the raw `createBulkFolder` request (a bare fetch, not tied to React's
 * lifecycle) and, on success, invalidates the folder tree + case lists using the
 * app-level queryClient — both of which outlive the modal. The returned promise
 * lets the caller surface a toast; rejection must be handled by the caller.
 */
export const useSaveGenerationInBackground = () => {
  const queryClient = useQueryClient();
  return (payload: any): Promise<void> =>
    createBulkFolder(payload).then(() => {
      invalidateFolderTree(queryClient);
      queryClient.invalidateQueries({ queryKey: ["allFolderTestCases"] });
      queryClient.invalidateQueries({ queryKey: ["allTestCases"] });
      queryClient.invalidateQueries({ queryKey: ["versionTestCasesAll"] });
    });
};

export const useUpdateFolder = (folderId: number) => {
  return useMutation({
    mutationFn: (payload: any) => updateFolder(folderId, payload),
  });
}; //

export const useMoveExplorerItems = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MoveExplorerItemsPayload) =>
      moveExplorerItems(payload),
    onSuccess: () => {
      invalidateFolderTree(queryClient);
      invalidateTestCaseLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ["testCaseDetails"] });
    },
  });
};

export const useCreateComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => createTestCaseComments(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["defectComments"] });
      queryClient.invalidateQueries({ queryKey: ["comments"] });
      // History is written asynchronously on the backend. Invalidate immediately
      // (covers the already-written case) and again shortly after to catch the
      // async write, instead of relying on a single best-guess delay.
      queryClient.invalidateQueries({ queryKey: ["history"] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["history"] });
      }, 600);
    },
  });
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      updateTestCaseComments(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["defectComments"] });
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    },
  });
};

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => deleteTestCaseComments(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["defectComments"] });
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    },
  });
};

export const useTogglePinComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => togglePinComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["defectComments"] });
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    },
  });
};

// --- Optimistic temp-id reconciliation for nested step creation ---------------
// When a step is created optimistically it gets a client-only temp id. If the
// user immediately adds a CHILD step inside it (e.g. "Add a Step Inside" a FOR
// loop) before the parent's create has returned a real server id, the child's
// parentId would still point at the parent's temp id. The backend can't resolve
// that temp id and (by design) strips parent_id to null — so the child lands at
// the top level instead of nested. To avoid this we track temp→real id mappings
// and the in-flight parent create promises, then rewrite the child's parentId to
// the real server id (awaiting the parent's create if it's still pending) before
// the request is sent.
// Temp ids are globally unique negative numbers, but the temp→real and
// in-flight maps are scoped per test case so concurrently open test cases in
// the same tab never share state and each case's entries are cleaned up
// independently (no global FIFO eviction dropping an entry another case needs).
interface CaseStepRegistry {
  tempToReal: Map<number, number>;
  // Reverse mapping (real server id → the temp id it replaced). Used purely so
  // the UI can keep a STABLE React key across the optimistic→real swap: the
  // optimistic row renders with key = tempId; once the refetch brings the real
  // (positive) id, looking it up here returns the same tempId so the row patches
  // in place instead of remounting (which would flash/"flicker" the new step).
  realToTemp: Map<number, number>;
  inFlight: Map<number, Promise<number>>;
}
const caseStepRegistries = new Map<string, CaseStepRegistry>();
const getCaseStepRegistry = (testCaseId: string): CaseStepRegistry => {
  let reg = caseStepRegistries.get(testCaseId);
  if (!reg) {
    reg = { tempToReal: new Map(), realToTemp: new Map(), inFlight: new Map() };
    caseStepRegistries.set(testCaseId, reg);
  }
  return reg;
};
const releaseCaseStepRegistryIfEmpty = (testCaseId: string) => {
  const reg = caseStepRegistries.get(testCaseId);
  if (reg && reg.tempToReal.size === 0 && reg.inFlight.size === 0) {
    caseStepRegistries.delete(testCaseId);
  }
};

// Resolve a STABLE React key for a rendered step. If the step's (real) id was
// produced by an optimistic create, return the original temp id so the row keeps
// the same key it had while optimistic — preventing a remount/flicker when the
// post-create refetch swaps the temp id for the real one. Falls back to the id.
export const getStableStepKey = (
  testCaseId: string | number,
  stepId: number,
): number => {
  const reg = caseStepRegistries.get(String(testCaseId));
  const temp = reg?.realToTemp.get(stepId);
  return temp ?? stepId;
};
let stepTempIdSeq = 0;
const nextStepTempId = () => -++stepTempIdSeq; // negative ids never collide with real (positive) ones

type Deferred<T> = {
  promise: Promise<T>;
  resolve: (v: T) => void;
  reject: (e: unknown) => void;
};
const createDeferred = <T>(): Deferred<T> => {
  let resolve!: (v: T) => void;
  let reject!: (e: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

const resolveRealParentId = async (
  parentId: unknown,
  testCaseId: string,
): Promise<unknown> => {
  if (typeof parentId !== "number") return parentId;
  const reg = getCaseStepRegistry(testCaseId);
  if (reg.tempToReal.has(parentId)) return reg.tempToReal.get(parentId);
  if (reg.inFlight.has(parentId)) {
    try {
      return await reg.inFlight.get(parentId);
    } catch {
      return parentId; // parent create failed — let the backend guard handle it
    }
  }
  return parentId; // already a real id
};

// Resolve an id selected for deletion to its real (positive) server id before
// the request is sent, awaiting the create if it is still in flight.
//
// Bug this fixes: when the user bulk-selects steps while the recorder is still
// streaming steps in (optimistic rows), some selected ids are client-only
// negative temp ids whose create hasn't returned a real server id yet. The
// backend's bulk-delete endpoint silently filters out any non-positive id
// (see testStepController.bulkDeleteTestSteps), so those rows were never
// actually deleted — only the already-synced (real-id) rows were. The
// optimistic onMutate removal still fired a "deleted successfully" toast and
// hid ALL selected rows instantly, so the user saw success. But once the
// still-pending create(s) settled, their own onSettled invalidate refetched
// the authoritative list — which still contained those never-deleted rows —
// and they reappeared.
// Returns null when there is nothing to delete server-side (the create failed,
// or the temp id is unknown/already released), so the caller can drop it.
const resolveRealStepId = async (
  rawId: unknown,
  testCaseId: string,
): Promise<number | null> => {
  const id = Number(rawId);
  if (!Number.isFinite(id)) return null;
  if (id > 0) return id; // already a real server id
  const reg = getCaseStepRegistry(testCaseId);
  if (reg.tempToReal.has(id)) return reg.tempToReal.get(id) ?? null;
  if (reg.inFlight.has(id)) {
    try {
      return await reg.inFlight.get(id)!;
    } catch {
      return null; // the create itself failed — nothing exists server-side
    }
  }
  return null; // unknown temp id (e.g. registry already released) — nothing to delete
};

// Recursively insert a temp step into its parent's childSteps array (positioned),
// mirroring the server's nested shape. Returns true if the parent was found.
const insertIntoParentChildSteps = (
  nodes: any[],
  parentId: number,
  tempStep: any,
): boolean => {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!node || typeof node !== "object") continue;
    if (node.id === parentId) {
      const children = Array.isArray(node.childSteps)
        ? [...node.childSteps]
        : [];
      const pos = tempStep.position;
      const idx =
        typeof pos === "number"
          ? children.findIndex(
              (c: any) => typeof c.position === "number" && c.position >= pos,
            )
          : -1;
      if (idx === -1) children.push(tempStep);
      else children.splice(idx, 0, tempStep);
      nodes[i] = { ...node, childSteps: children };
      return true;
    }
    if (Array.isArray(node.childSteps) && node.childSteps.length) {
      const cloned = [...node.childSteps];
      if (insertIntoParentChildSteps(cloned, parentId, tempStep)) {
        nodes[i] = { ...node, childSteps: cloned };
        return true;
      }
    }
  }
  return false;
};

export const useCreateTestSteps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ payload }: { payload: any }) => {
      // Strip client-only fields and rewrite an optimistic parentId to the real
      // server id (awaiting the parent's create if it is still in flight).
      const { _tempId, _optimistic, ...apiPayload } = payload;
      const testCaseId = String(payload.testCaseId);
      const reg = getCaseStepRegistry(testCaseId);
      if (apiPayload.parentId != null) {
        apiPayload.parentId = await resolveRealParentId(
          apiPayload.parentId,
          testCaseId,
        );
      }
      const res = await createTestSteps(apiPayload);
      const realId = Number((res as any)?.data?.id);
      if (typeof _tempId === "number" && Number.isFinite(realId)) {
        reg.tempToReal.set(_tempId, realId);
        reg.realToTemp.set(realId, _tempId);
        // Bound the per-case map so long-lived sessions don't leak.
        if (reg.tempToReal.size > 500) {
          const oldest = reg.tempToReal.keys().next().value;
          if (oldest !== undefined) {
            const stale = reg.tempToReal.get(oldest);
            reg.tempToReal.delete(oldest);
            if (stale !== undefined) reg.realToTemp.delete(stale);
          }
        }
      }
      return res;
    },
    onMutate: async ({ payload }) => {
      const testCaseId = String(payload.testCaseId);
      const queryKey = ["testSteps", testCaseId];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      // Assign a stable temp id shared by the optimistic cache row and the
      // mutationFn (so children can later be remapped to the real id). Register
      // an in-flight promise immediately so a child added before this create
      // resolves can await the real parent id.
      const tempId = nextStepTempId();
      payload._tempId = tempId;
      const deferred = createDeferred<number>();
      getCaseStepRegistry(testCaseId).inFlight.set(tempId, deferred.promise);

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.data?.content) return old;
        const tempStep = { ...payload, id: tempId, _optimistic: true };
        const insertPos = payload?.position;
        if (typeof insertPos === "number") tempStep.position = insertPos;

        // Nested create: insert into the parent's childSteps so it renders
        // nested immediately and we don't disturb top-level positions.
        if (payload?.parentId != null) {
          const content = [...old.data.content];
          if (insertIntoParentChildSteps(content, payload.parentId, tempStep)) {
            return { ...old, data: { ...old.data, content } };
          }
          // Parent not found in the tree — fall through to a top-level append.
        }

        // Append when no explicit position (recorder-style add at the end).
        if (insertPos == null) {
          return {
            ...old,
            data: { ...old.data, content: [...old.data.content, tempStep] },
          };
        }

        // Insert in the middle: bump positions of top-level steps at/after the
        // insert point so numbering stays correct, then splice the temp step in
        // at the right array index (content is returned sorted by position).
        const bumped = old.data.content.map((s: any) =>
          typeof s.position === "number" && s.position >= insertPos
            ? { ...s, position: s.position + 1 }
            : s,
        );
        const idx = bumped.findIndex(
          (s: any) => typeof s.position === "number" && s.position > insertPos,
        );
        if (idx === -1) bumped.push(tempStep);
        else bumped.splice(idx, 0, tempStep);

        return { ...old, data: { ...old.data, content: bumped } };
      });
      return { previous, queryKey, tempId, deferred, testCaseId };
    },
    onSuccess: (data, _vars, context: any) => {
      // Unblock any child create waiting on this parent's real id.
      const realId = Number((data as any)?.data?.id);
      if (context?.deferred) {
        if (Number.isFinite(realId)) context.deferred.resolve(realId);
        else context.deferred.reject(new Error("missing created step id"));
      }
    },
    onError: (_err, _vars, context: any) => {
      if (context?.deferred) context.deferred.reject(_err);
      if (context?.previous)
        queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.tempId != null && context?.testCaseId != null) {
        const reg = caseStepRegistries.get(context.testCaseId);
        reg?.inFlight.delete(context.tempId);
        releaseCaseStepRegistryIfEmpty(context.testCaseId);
      }
      if (context?.queryKey)
        queryClient.invalidateQueries({ queryKey: context.queryKey });
    },
  });
};

export const useUpdateTestSteps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      updateTestSteps(id, payload),
    onMutate: async ({ id, payload }) => {
      const testCaseId = String(payload.testCaseId);
      const queryKey = ["testSteps", testCaseId];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.data?.content) return old;
        return {
          ...old,
          data: {
            ...old.data,
            content: old.data.content.map((step: any) =>
              step.id === id ? { ...step, ...payload } : step,
            ),
          },
        };
      });
      return { previous, queryKey };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous)
        queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.queryKey)
        queryClient.invalidateQueries({ queryKey: context.queryKey });
    },
  });
};

export const useFetchRestResponse = () => {
  return useMutation({
    mutationFn: (payload: any) => fetchRestResponse(payload),
  });
};

// Delete Test case
export const useDeleteTestCaseById = (testCaseId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deletTestCaseById(testCaseId),
    onSuccess: () => {
      invalidateTestCaseLists(queryClient);
      queryClient.invalidateQueries({ queryKey: ["projectOverview"] });
      queryClient.invalidateQueries({ queryKey: ["projectSummary"] });
      queryClient.removeQueries({
        queryKey: ["testCaseDetails", String(testCaseId)],
      });
    },
  });
};

// Accepts either the legacy plain-number arg (manual test steps always have a
// real server id, never a recorder temp id) or `{ id, testCaseId }` so the
// automation step list can additionally resolve a still-in-flight temp id.
type DeleteStepArg = number | { id: number; testCaseId?: string | number };
const normalizeDeleteStepArg = (
  arg: DeleteStepArg,
): { id: number; testCaseId: string | null } =>
  typeof arg === "number"
    ? { id: arg, testCaseId: null }
    : {
        id: arg.id,
        testCaseId: arg.testCaseId != null ? String(arg.testCaseId) : null,
      };

// Delete Test Step
export const useDeleteTestStepById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg: DeleteStepArg) => {
      const { id, testCaseId } = normalizeDeleteStepArg(arg);
      // Same temp-id race as useBulkDeleteTestSteps: a single-step delete
      // fired at a freshly-recorded (still-optimistic) row must resolve its
      // negative temp id to the real server id first, otherwise the request
      // silently no-ops and the row reappears once the pending create settles.
      const realId = testCaseId
        ? await resolveRealStepId(id, testCaseId)
        : id > 0
          ? id
          : null;
      if (realId == null) return undefined; // nothing exists server-side to delete
      return deleteTestStepById(realId);
    },
    // Optimistically remove the step from the cached list so the row
    // disappears instantly (mirrors useBulkDeleteTestSteps). Only ever called
    // for leaf steps — RenderStep routes steps with children to bulk delete —
    // so there is no orphan/cascade risk. Server re-sequencing is reconciled
    // by the onSettled invalidate.
    onMutate: async (arg: DeleteStepArg) => {
      const { id: testStepId } = normalizeDeleteStepArg(arg);
      const queries = queryClient.getQueriesData<any>({
        queryKey: ["testSteps"],
      });
      let queryKey: string[] | null = null;
      let previous: any = null;
      for (const [key, data] of queries) {
        if (data?.data?.content?.some((s: any) => s.id === testStepId)) {
          queryKey = key as string[];
          previous = data;
          break;
        }
      }
      if (!queryKey) return { previous: null, queryKey: null };
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.data?.content) return old;
        return {
          ...old,
          data: {
            ...old.data,
            content: old.data.content.filter(
              (step: any) => step.id !== testStepId,
            ),
          },
        };
      });
      return { previous, queryKey };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous)
        queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.queryKey)
        queryClient.invalidateQueries({ queryKey: context.queryKey });
    },
  });
};

export const useBulkUpdateTestSteps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: Record<string, any>) => bulkUpdateTestSteps(params),
    onMutate: async (params) => {
      const ids: number[] = params.ids || [];
      // Find testCaseId from cached steps
      const queries = queryClient.getQueriesData<any>({
        queryKey: ["testSteps"],
      });
      let queryKey: string[] | null = null;
      let previous: any = null;
      for (const [key, data] of queries) {
        if (data?.data?.content?.some((s: any) => ids.includes(s.id))) {
          queryKey = key as string[];
          previous = data;
          break;
        }
      }
      if (!queryKey) return { previous: null, queryKey: null };
      await queryClient.cancelQueries({ queryKey });
      const { ids: _ids, ...updates } = params;
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.data?.content) return old;
        return {
          ...old,
          data: {
            ...old.data,
            content: old.data.content.map((step: any) =>
              ids.includes(step.id) ? { ...step, ...updates } : step,
            ),
          },
        };
      });
      return { previous, queryKey };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous)
        queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.queryKey)
        queryClient.invalidateQueries({ queryKey: context.queryKey });
    },
  });
};

export const useBulkUpdateSteps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ steps }: { testCaseId: string; steps: any[] }) =>
      bulkUpdateSteps(steps),
    onMutate: async ({
      testCaseId: rawTestCaseId,
      steps,
    }: {
      testCaseId: string;
      steps: any[];
    }) => {
      if (!steps.length) return { previous: null, queryKey: null };
      const testCaseId = String(rawTestCaseId);
      const queryKey = ["testSteps", testCaseId];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.data?.content) return old;
        const updatedMap = new Map(steps.map((s: any) => [s.id, s]));
        const updateAndSort = (siblings: any[]): any[] =>
          siblings
            .map((step: any) => {
              const updated = updatedMap.get(step.id);
              const nextStep = updated ? { ...step, ...updated } : step;
              if (Array.isArray(step.childSteps)) {
                nextStep.childSteps = updateAndSort(step.childSteps);
              }
              if (Array.isArray(step.testStepDTOS)) {
                nextStep.testStepDTOS = updateAndSort(step.testStepDTOS);
              }
              return nextStep;
            })
            .sort(
              (a: any, b: any) =>
                Number(a.position ?? 0) - Number(b.position ?? 0),
            );
        return {
          ...old,
          data: {
            ...old.data,
            content: updateAndSort(old.data.content),
          },
        };
      });
      return { previous, queryKey };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous)
        queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.queryKey)
        queryClient.invalidateQueries({ queryKey: context.queryKey });
    },
  });
};

export const useBulkUpdateManualTestSteps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (testSteps: Partial<TestSteps>[]) =>
      bulkUpdateManualTestSteps(testSteps),
    // Optimistically patch the cached test steps so an edit survives navigating
    // away and back. Without this, the manual editor's updates (step text,
    // expected result, reorder) were persisted server-side but never written to
    // the ["testSteps", testCaseId] cache. With staleTime = 2 min on that query,
    // switching to another test case and back within 2 minutes served the stale
    // pre-edit cache — the user's edits appeared to "disappear" (issue #1926).
    onMutate: async (testSteps) => {
      const updates = (testSteps ?? []).filter((s) => (s as any)?.id != null);
      if (updates.length === 0)
        return { affected: [] as { queryKey: any; previous: any }[] };
      const byId = new Map<number, Partial<TestSteps>>();
      for (const u of updates) byId.set(Number((u as any).id), u);

      const queries = queryClient.getQueriesData<any>({
        queryKey: ["testSteps"],
      });
      const affected: { queryKey: any; previous: any }[] = [];

      for (const [queryKey, data] of queries) {
        if (!data?.data?.content?.some((s: any) => byId.has(Number(s.id))))
          continue;
        await queryClient.cancelQueries({ queryKey });
        affected.push({ queryKey, previous: data });
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old?.data?.content) return old;
          return {
            ...old,
            data: {
              ...old.data,
              content: old.data.content.map((step: any) => {
                const upd = byId.get(Number(step.id));
                if (!upd) return step;
                const patched: any = { ...step };
                if ((upd as any).manualTestSteps !== undefined)
                  patched.manualTestSteps = (upd as any).manualTestSteps;
                if ((upd as any).expectedResults !== undefined)
                  patched.expectedResults = (upd as any).expectedResults;
                if ((upd as any).position !== undefined)
                  patched.position = (upd as any).position;
                return patched;
              }),
            },
          };
        });
      }
      return { affected };
    },
    onError: (error, _vars, context: any) => {
      context?.affected?.forEach(({ queryKey, previous }: any) =>
        queryClient.setQueryData(queryKey, previous),
      );
      console.error("Failed to update test steps:", error);
    },
    onSettled: (_data, _err, _vars, context: any) => {
      context?.affected?.forEach(({ queryKey }: any) =>
        queryClient.invalidateQueries({ queryKey }),
      );
    },
  });
};

// Bulk Delete Test Step

export const useBulkDeleteTestSteps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: Record<string, any>) => {
      const rawIds: number[] = params.ids || [];
      const testCaseId =
        params.testCaseId != null ? String(params.testCaseId) : null;
      // Resolve any still-in-flight/optimistic (negative) temp ids to their
      // real server id — awaiting the pending create if necessary (see
      // resolveRealStepId). Without this, selecting a just-recorded step
      // before its create finishes sends its temp id straight to the
      // backend, which silently ignores it, so that step never actually gets
      // deleted even though the UI optimistically reports success.
      const resolved = testCaseId
        ? await Promise.all(rawIds.map((id) => resolveRealStepId(id, testCaseId)))
        : rawIds;
      const realIds = Array.from(
        new Set(
          resolved.filter(
            (id): id is number => typeof id === "number" && id > 0,
          ),
        ),
      );
      if (realIds.length === 0) return undefined; // nothing exists server-side to delete
      if (testCaseId) {
        // A racing create's own onSettled invalidate can refetch and
        // reintroduce one of these rows (now under its real id) while we were
        // awaiting resolution above. Strip them again right before the
        // request so the UI doesn't briefly flash the step back in.
        const queryKey = ["testSteps", testCaseId];
        queryClient.setQueryData(queryKey, (old: any) => {
          if (!old?.data?.content) return old;
          return {
            ...old,
            data: {
              ...old.data,
              content: old.data.content.filter(
                (step: any) => !realIds.includes(step.id),
              ),
            },
          };
        });
      }
      return bulkDeleteTestSteps({ ids: realIds });
    },
    onMutate: async (params) => {
      const ids: number[] = params.ids || [];
      const queries = queryClient.getQueriesData<any>({
        queryKey: ["testSteps"],
      });
      let queryKey: string[] | null = null;
      let previous: any = null;
      for (const [key, data] of queries) {
        if (data?.data?.content?.some((s: any) => ids.includes(s.id))) {
          queryKey = key as string[];
          previous = data;
          break;
        }
      }
      if (!queryKey) return { previous: null, queryKey: null };
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.data?.content) return old;
        return {
          ...old,
          data: {
            ...old.data,
            content: old.data.content.filter(
              (step: any) => !ids.includes(step.id),
            ),
          },
        };
      });
      return { previous, queryKey };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous)
        queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (_data, _err, _vars, context: any) => {
      if (context?.queryKey)
        queryClient.invalidateQueries({ queryKey: context.queryKey });
    },
  });
};

export const useDeletetestcasetypeById = (testcasetypeid: number) => {
  return useMutation({
    mutationFn: () => deleteTestCasetypebyId(testcasetypeid),
  });
};

export const useUpdateTestCaseTypesById = (prioritiesid: number) => {
  return useMutation({
    mutationFn: (payload: any) =>
      updateTestCasetypesById(prioritiesid, payload),
  });
};

export const useDeleteElementById = () => {
  return useMutation({
    mutationFn: (elementId: number | string) => deleteElementById(elementId),
  });
};

export const useExportTestCasesExcel = () => {
  return useMutation({
    mutationFn: (versionId: number) => exportTestCasesExcel(versionId),
  });
};

export const useExportSelectedTestCasesExcel = () => {
  return useMutation({
    mutationFn: (ids: number[]) => exportSelectedTestCasesExcel(ids),
  });
};

export const useImportTestCasesExcel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, folderId }: { file: File; folderId?: number }) =>
      importTestCasesExcel(file, folderId),
    onSuccess: () => {
      invalidateTestCaseLists(queryClient);
    },
  });
};

export const useExportStepGroupsXml = () => {
  return useMutation({
    mutationFn: (options: Record<string, any>) => exportStepGroupsXml(options),
  });
};

// ── Manual Test Runs ──────────────────────────────────────────────────────────

export const useCreateManualTestRun = () =>
  useMutation({
    mutationFn: ({ testCaseId }: { testCaseId: number }) =>
      createManualTestRun(testCaseId),
  });

export const useUpdateManualRunStep = () =>
  useMutation({
    mutationFn: ({
      stepId,
      status,
      comment,
      imageUrl,
    }: {
      stepId: number;
      status: string;
      comment?: string;
      imageUrl?: string;
    }) => updateManualTestRunStep(stepId, status, comment, imageUrl),
  });

export const useCompleteManualTestRun = () =>
  useMutation({
    mutationFn: ({ runId, notes }: { runId: number; notes?: string }) =>
      completeManualTestRun(runId, notes),
  });

export const useDeleteManualTestRun = () =>
  useMutation({
    mutationFn: ({ runId }: { runId: number }) => deleteManualTestRun(runId),
  });

export const useUploadManualRunImage = () =>
  useMutation({
    mutationFn: (file: File) => uploadManualRunImage(file),
  });
